import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { surveyService } from '../../services/surveyService';
import { surveyResponseService } from '../../services/surveyResponseService';
import {
  CheckCircleIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  DocumentTextIcon,
  QuestionMarkCircleIcon,
  ExclamationCircleIcon,
  PlayIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  ClockIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';

const PublicSurveyForm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Handle both parameter formats: 'id' and 'k' OR 'l'
  const surveyId = searchParams.get('id');
  const linkKey = searchParams.get('k') || searchParams.get('l'); // Support both 'k' and 'l'
  const encodedEmployeeId = searchParams.get('eid'); // Get encoded employee ID
  const surveyType = searchParams.get('type') || 'public';

  const [survey, setSurvey] = useState(null);
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [showStartScreen, setShowStartScreen] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // New state for survey status
  const [surveyStatus, setSurveyStatus] = useState({
    isCompleted: false,
    completedDate: null,
    canSubmit: true
  });
  
  const topRef = useRef(null);

  useEffect(() => {
    console.log('🔍 URL Parameters:', {
      surveyId,
      linkKey,
      encodedEmployeeId,
      surveyType,
      allParams: Object.fromEntries(searchParams.entries())
    });

    if (surveyId && linkKey) {
      checkSurveyStatus();
    } else {
      console.error('❌ Missing required parameters:', { surveyId, linkKey });
      setError('Invalid survey link. Missing required parameters. Please check the URL.');
      setLoading(false);
    }
  }, [surveyId, linkKey]);

  useEffect(() => {
    scrollToTop();
  }, [currentCategoryIndex]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Check if survey is already completed before loading
  const checkSurveyStatus = async () => {
    try {
      console.log('🔍 Checking survey status...');
      
      // First, check if survey is already completed using the service
      const status = await surveyResponseService.getPublicSurveyStatus(surveyId, linkKey, encodedEmployeeId);
      console.log('📊 Survey status:', status);
      
      if (status.isCompleted) {
        setSurveyStatus({
          isCompleted: true,
          completedDate: status.completedDate,
          canSubmit: false
        });
        setLoading(false);
        return;
      }
      
      // If not completed, load the survey
      await loadSurvey();
      
    } catch (err) {
      console.error('❌ Error checking survey status:', err);
      // Continue to load survey even if status check fails
      await loadSurvey();
    }
  };

  const loadSurvey = async () => {
    try {
      console.log('🔄 Loading public survey:', { surveyId, linkKey, surveyType });
      setLoading(true);
      setError('');
      
      // Use the public survey service that validates the link key
      const surveyData = await surveyService.getPublicSurvey(surveyId, linkKey);
      console.log('✅ Public survey data received:', surveyData);
      
      if (surveyData && surveyData.survey) {
        const normalizedSurvey = {
          Id: surveyData.survey.id,
          Name: surveyData.survey.name,
          Description: surveyData.survey.description,
          Categories: (surveyData.survey.categories || []).map(category => ({
            Id: category.id,
            Name: category.name,
            Description: category.description,
            Order: category.order,
            Questions: (category.questions || []).map(question => ({
              Id: question.id,
              QuestionText: question.questionText,
              Type: question.type,
              Order: question.order,
              IsRequired: question.isRequired,
              Placeholder: question.placeholder,
              HelpText: question.helpText,
              Options: question.options,
              MinValue: question.minValue,
              MaxValue: question.maxValue
            }))
          }))
        };
        
        if (normalizedSurvey.Name && Array.isArray(normalizedSurvey.Categories)) {
          setSurvey(normalizedSurvey);
          setSurveyStatus(prev => ({ ...prev, canSubmit: true }));
        } else {
          throw new Error('Survey data missing required properties');
        }
      } else {
        throw new Error('No survey data found');
      }
    } catch (err) {
      console.error('❌ Error loading public survey:', err);
      setError(err.message || 'Failed to load survey. The link may be invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
    
    // Clear validation error for this question when user starts answering
    if (validationErrors[questionId]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[questionId];
        return newErrors;
      });
    }
  };

  const validateCurrentCategory = () => {
    if (!survey || !survey.Categories[currentCategoryIndex]) return true;
    
    const currentCategory = survey.Categories[currentCategoryIndex];
    const errors = {};
    let isValid = true;

    currentCategory.Questions.forEach(question => {
      // Skip validation for heading and paragraph types
      if (question.Type === 0 || question.Type === 14) return;
      
      if (question.IsRequired) {
        const answer = answers[question.Id];
        
        if (answer === undefined || answer === null || answer === '') {
          errors[question.Id] = 'This question is required';
          isValid = false;
        } else if (Array.isArray(answer) && answer.length === 0) {
          errors[question.Id] = 'Please select at least one option';
          isValid = false;
        } else if (question.Type === 7 && (answer < question.MinValue || answer > question.MaxValue)) {
          errors[question.Id] = `Please enter a value between ${question.MinValue} and ${question.MaxValue}`;
          isValid = false;
        }
      }
    });

    setValidationErrors(errors);
    return isValid;
  };

  const nextCategory = () => {
    if (validateCurrentCategory()) {
      if (survey && currentCategoryIndex < survey.Categories.length - 1) {
        setCurrentCategoryIndex(prev => prev + 1);
      } else {
        handleSubmitSurvey();
      }
    } else {
      // Scroll to first error
      const firstErrorElement = document.querySelector('[data-error="true"]');
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const prevCategory = () => {
    if (currentCategoryIndex > 0) {
      setCurrentCategoryIndex(prev => prev - 1);
    }
  };

const handleSubmitSurvey = async () => {
  if (!surveyStatus.canSubmit) {
    setError('This survey has already been completed and cannot be submitted again.');
    return;
  }

  try {
    setSubmitting(true);
    setError('');

    const submissionData = {
      SurveyId: parseInt(surveyId),
      Token: linkKey,
      EncodedEmployeeId: encodedEmployeeId,
      Responses: Object.keys(answers).map(questionId => {
        const answer = answers[questionId];
        
        // Format the answer based on type with proper defaults
        let formattedAnswer = '';
        let ratingValue = null;
        let selectedOptions = [];

        if (Array.isArray(answer)) {
          formattedAnswer = answer.join(', ');
          selectedOptions = answer;
        } else if (typeof answer === 'number') {
          formattedAnswer = answer.toString();
          ratingValue = answer;
        } else if (typeof answer === 'string') {
          formattedAnswer = answer;
        } else if (answer !== null && answer !== undefined) {
          formattedAnswer = String(answer);
        }

        return {
          QuestionId: parseInt(questionId),
          Answer: formattedAnswer,
          RatingValue: ratingValue,
          SelectedOptions: selectedOptions, // Always an array, never null
          FileUrl: "",
          FileName: "", 
          FileSize: 0
        };
      })
    };

    console.log('📦 Final submission data:', JSON.stringify(submissionData, null, 2));

    const result = await surveyResponseService.submitPublicSurveyResponse(submissionData);
    console.log('✅ Survey submitted successfully:', result);
    
    setSurveyStatus({
      isCompleted: true,
      completedDate: new Date().toISOString(),
      canSubmit: false
    });
    
    scrollToTop();
    
  } catch (err) {
    console.error('❌ Error submitting survey:', err);
    setError(err.message || 'Failed to submit survey. Please try again.');
  } finally {
    setSubmitting(false);
  }
};
  const startSurvey = () => {
    if (surveyStatus.isCompleted) {
      setError('This survey has already been completed. You cannot start it again.');
      return;
    }
    setShowStartScreen(false);
    scrollToTop();
  };

  const getProgressPercentage = () => {
    if (!survey || !survey.Categories.length) return 0;
    return ((currentCategoryIndex + 1) / survey.Categories.length) * 100;
  };

  const getTotalQuestions = () => {
    if (!survey?.Categories) return 0;
    return survey.Categories.reduce((total, category) => 
      total + (category.Questions?.length || 0), 0
    );
  };

  const getAnsweredQuestions = () => {
    return Object.keys(answers).length;
  };

  const getCurrentCategoryQuestions = () => {
    if (!survey?.Categories[currentCategoryIndex]) return [];
    return survey.Categories[currentCategoryIndex].Questions || [];
  };

  const isNextButtonDisabled = () => {
    const currentQuestions = getCurrentCategoryQuestions();
    const requiredQuestions = currentQuestions.filter(q => q.IsRequired && q.Type !== 0 && q.Type !== 14);
    
    return requiredQuestions.some(question => {
      const answer = answers[question.Id];
      return answer === undefined || answer === null || answer === '' || 
             (Array.isArray(answer) && answer.length === 0);
    });
  };

  // Get survey type display name and icon
  const getSurveyTypeInfo = () => {
    switch (surveyType) {
      case 'private':
        return {
          name: 'Employee Survey',
          icon: UserGroupIcon,
          description: 'Confidential employee feedback survey',
          color: 'blue'
        };
      case 'public':
        return {
          name: 'Team Survey', 
          icon: BuildingOfficeIcon,
          description: 'Team distribution and feedback survey',
          color: 'green'
        };
      default:
        return {
          name: 'Survey',
          icon: DocumentTextIcon,
          description: 'Online survey form',
          color: 'gray'
        };
    }
  };

  const surveyTypeInfo = getSurveyTypeInfo();
  const SurveyTypeIcon = surveyTypeInfo.icon;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900">Loading Survey...</h2>
          <p className="text-gray-600 mt-2">Please wait while we load your survey</p>
        </div>
      </div>
    );
  }

  if (error && !surveyStatus.isCompleted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-sm border border-red-200 p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <QuestionMarkCircleIcon className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Survey Not Available</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <div className="text-sm text-gray-500 mb-6">
            <p>Current URL: {window.location.href}</p>
            <p>Required parameters: id, k (or l), and eid</p>
          </div>
          <button 
            onClick={() => window.location.reload()}
            className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Show completed screen if survey is already completed
  if (surveyStatus.isCompleted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-sm border border-green-200 p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircleIcon className="w-10 h-10 text-green-600" />
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Survey Completed Successfully!</h2>
          <p className="text-gray-600 mb-4">
            Thank you for your participation! Your responses have been recorded.
          </p>
          
          {surveyStatus.completedDate && (
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6">
              <p className="text-gray-700 text-sm">
                Completed on: {new Date(surveyStatus.completedDate).toLocaleDateString()} at{' '}
                {new Date(surveyStatus.completedDate).toLocaleTimeString()}
              </p>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-2 text-blue-700">
              <LockClosedIcon className="w-4 h-4" />
              <span className="text-sm font-medium">Submission Complete</span>
            </div>
            <p className="text-blue-600 text-xs mt-1">
              Your responses have been securely submitted and cannot be modified.
            </p>
          </div>

          <button 
            onClick={() => window.close()}
            className="w-full px-4 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Close Window
          </button>
        </div>
      </div>
    );
  }

  if (!survey) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-sm border border-blue-200 p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <DocumentTextIcon className="w-8 h-8 text-blue-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Survey Not Found</h3>
          <p className="text-gray-600 mb-6">The requested survey could not be loaded.</p>
        </div>
      </div>
    );
  }

  if (showStartScreen) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-8 max-w-2xl w-full text-center">
          {/* Survey Type Header */}
          <div className="mb-8">
            <div className={`w-20 h-20 bg-gradient-to-br from-${surveyTypeInfo.color}-500 to-${surveyTypeInfo.color}-600 rounded-2xl flex items-center justify-center mx-auto mb-4`}>
              <SurveyTypeIcon className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">{surveyTypeInfo.name}</h2>
            <p className="text-gray-600 mt-2">{surveyTypeInfo.description}</p>
          </div>

          {/* Survey Information */}
          <div className="bg-gray-50 rounded-xl p-6 mb-8 border border-gray-200">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">{survey.Name}</h1>
            {survey.Description && (
              <p className="text-gray-600 text-lg leading-relaxed">{survey.Description}</p>
            )}
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="text-2xl font-bold text-blue-600">{survey.Categories?.length || 0}</div>
                <div className="text-sm text-gray-600">Sections</div>
              </div>
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="text-2xl font-bold text-blue-600">{getTotalQuestions()}</div>
                <div className="text-sm text-gray-600">Questions</div>
              </div>
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="text-2xl font-bold text-blue-600">5-10 min</div>
                <div className="text-sm text-gray-600">Est. Time</div>
              </div>
            </div>
          </div>

          {/* Security Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-2 text-blue-700 mb-2">
              <LockClosedIcon className="w-4 h-4" />
              <span className="font-medium">One-time Access</span>
            </div>
            <p className="text-blue-600 text-sm">
              This survey link can only be used once. Make sure you have enough time to complete it in one session.
            </p>
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8 text-left">
            <h3 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
              <ExclamationCircleIcon className="w-5 h-5" />
              Survey Instructions
            </h3>
            <ul className="space-y-2 text-blue-700 text-sm">
              <li>• All questions marked with <span className="text-red-500">*</span> are required</li>
              <li>• Please provide honest and thoughtful responses</li>
              <li>• Your feedback is anonymous and confidential</li>
              <li>• Complete all required questions before proceeding</li>
              <li>• You can navigate between sections using Previous/Next buttons</li>
              <li>• <strong>This survey can only be submitted once</strong></li>
            </ul>
          </div>

          {/* Start Button */}
          <button 
            onClick={startSurvey}
            className="px-8 py-4 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors flex items-center gap-3 mx-auto text-lg font-semibold shadow-lg hover:shadow-xl"
          >
            <PlayIcon className="w-6 h-6" />
            Start Survey
          </button>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-gray-500 text-sm">
              Your participation helps us improve and provide better experiences.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const currentCategory = survey.Categories[currentCategoryIndex];
  const totalQuestions = getTotalQuestions();
  const answeredQuestions = getAnsweredQuestions();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex flex-col">
      <div ref={topRef} />
      
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 flex-shrink-0">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 bg-gradient-to-br from-${surveyTypeInfo.color}-500 to-${surveyTypeInfo.color}-600 rounded-lg flex items-center justify-center`}>
                <SurveyTypeIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">{surveyTypeInfo.name}</h1>
                <p className="text-sm text-gray-600">{survey.Name}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm font-medium text-gray-700">
                Section {currentCategoryIndex + 1} of {survey.Categories.length}
              </div>
              <div className="text-xs text-gray-500">
                {answeredQuestions}/{totalQuestions} answered
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Progress Bar */}
          <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6 mb-6">
            <div className="flex justify-between items-center mb-3">
              <span className="text-sm font-medium text-gray-700">
                Progress: {Math.round(getProgressPercentage())}%
              </span>
              <span className="text-sm text-gray-500">
                {currentCategoryIndex + 1}/{survey.Categories.length}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className="bg-green-500 h-3 rounded-full transition-all duration-300"
                style={{ width: `${getProgressPercentage()}%` }}
              ></div>
            </div>
          </div>

          {/* Current Category */}
          <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6 mb-6">
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-blue-500 text-white rounded-lg w-12 h-12 flex items-center justify-center text-lg font-bold flex-shrink-0">
                {currentCategory.Order}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-2xl font-bold text-gray-900 mb-2 break-words">{currentCategory.Name}</h2>
                {currentCategory.Description && (
                  <p className="text-gray-600 break-words">{currentCategory.Description}</p>
                )}
              </div>
            </div>

            {/* Questions */}
            <div className="space-y-6">
              {currentCategory.Questions && currentCategory.Questions.length > 0 ? (
                currentCategory.Questions.map((question, qIndex) => (
                  <QuestionItem
                    key={question.Id}
                    question={question}
                    questionNumber={qIndex + 1}
                    categoryNumber={currentCategory.Order}
                    value={answers[question.Id]}
                    onChange={(value) => handleAnswerChange(question.Id, value)}
                    error={validationErrors[question.Id]}
                  />
                ))
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                  <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">No questions in this section.</p>
                </div>
              )}
            </div>
          </div>

          {/* Navigation */}
          <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6 sticky bottom-0 z-10">
            <div className="flex items-center justify-between">
              <button 
                className="flex items-center gap-2 px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={prevCategory}
                disabled={currentCategoryIndex === 0}
              >
                <ChevronLeftIcon className="w-4 h-4" />
                Previous
              </button>

              <div className="text-center">
                <div className="text-sm text-gray-600 mb-1">Current Section</div>
                <div className="flex items-center gap-2">
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-medium text-sm">
                    {currentCategory.Order}
                  </span>
                  <span className="text-gray-400">/</span>
                  <span className="text-gray-600">{survey.Categories.length}</span>
                </div>
              </div>

              {currentCategoryIndex < survey.Categories.length - 1 ? (
                <button 
                  className="flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={nextCategory}
                  disabled={isNextButtonDisabled()}
                >
                  Next Section
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              ) : (
                <button 
                  className="flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={nextCategory}
                  disabled={isNextButtonDisabled() || submitting || !surveyStatus.canSubmit}
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="w-4 h-4" />
                      Complete Survey
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Validation Summary */}
            {Object.keys(validationErrors).length > 0 && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-red-700 mb-2">
                  <ExclamationCircleIcon className="w-4 h-4" />
                  <span className="font-medium">Please complete all required questions before proceeding</span>
                </div>
                <ul className="text-sm text-red-600 space-y-1">
                  {Object.entries(validationErrors).slice(0, 3).map(([questionId, error]) => (
                    <li key={questionId}>• {error}</li>
                  ))}
                  {Object.keys(validationErrors).length > 3 && (
                    <li>• ...and {Object.keys(validationErrors).length - 3} more</li>
                  )}
                </ul>
              </div>
            )}

            {/* Security Notice */}
            {!surveyStatus.canSubmit && (
              <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-yellow-700">
                  <LockClosedIcon className="w-4 h-4" />
                  <span className="font-medium">This survey has already been completed and cannot be submitted again.</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// QuestionItem Component
const QuestionItem = ({ question, questionNumber, categoryNumber, value, onChange, error }) => {
  const renderInput = () => {
    const baseProps = {
      placeholder: question.Placeholder || 'Enter your answer...',
      className: `w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
        error ? 'border-red-300 bg-red-50' : 'border-gray-300'
      }`
    };

    switch (question.Type) {
      case 0: // Heading
        return (
          <div className="text-xl font-bold text-gray-900 py-4 border-b border-gray-200 break-words">
            {question.QuestionText}
          </div>
        );

      case 1: // RatingScale
        const ratingLabels = [
          "Strongly Disagree",
          "Disagree", 
          "Neutral",
          "Agree",
          "Strongly Agree"
        ];

        const ratingColors = [
          'bg-red-100 border-red-300 text-red-800',
          'bg-orange-100 border-orange-300 text-orange-800',
          'bg-yellow-100 border-yellow-300 text-yellow-800',
          'bg-blue-100 border-blue-300 text-blue-800',
          'bg-green-100 border-green-300 text-green-800'
        ];

        const selectedRatingColors = [
          'bg-red-500 border-red-600 text-white',
          'bg-orange-500 border-orange-600 text-white',
          'bg-yellow-500 border-yellow-600 text-white',
          'bg-blue-500 border-blue-600 text-white',
          'bg-green-500 border-green-600 text-white'
        ];

        return (
          <div className="w-full overflow-hidden">
            <div className="grid grid-cols-5 gap-1 sm:gap-2 mb-2 sm:mb-3">
              {[1, 2, 3, 4, 5].map((rating) => (
                <label key={rating} className="flex flex-col items-center cursor-pointer">
                  <input
                    type="radio"
                    name={`question-${question.Id}`}
                    value={rating}
                    checked={value === rating}
                    onChange={() => onChange(rating)}
                    className="sr-only"
                  />
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-lg border-2 flex items-center justify-center font-semibold text-sm transition-all ${
                    value === rating 
                      ? `${selectedRatingColors[rating-1]} shadow-md scale-105` 
                      : `${ratingColors[rating-1]} hover:shadow-md hover:scale-105`
                  }`}>
                    {rating}
                  </div>
                </label>
              ))}
            </div>
            
            <div className="grid grid-cols-5 gap-1 sm:gap-2">
              {ratingLabels.map((label, index) => (
                <span 
                  key={index} 
                  className="text-[10px] xs:text-xs text-gray-600 text-center leading-tight font-medium break-words px-0.5"
                >
                  {label}
                </span>
              ))}
            </div>
          </div>
        );

      case 2: // TextField
        return (
          <input 
            type="text" 
            {...baseProps}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          />
        );

      case 3: // DateField
        return (
          <input 
            type="date" 
            {...baseProps}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          />
        );

      case 4: // TextArea
        return (
          <textarea 
            rows={4} 
            {...baseProps}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className={`${baseProps.className} resize-vertical min-h-[100px]`}
          />
        );

      case 5: // RadioButtons
        return (
          <div className="space-y-3">
            {question.Options?.map((option, index) => (
              <label key={index} className="flex items-center gap-3 p-3 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                <input
                  type="radio"
                  name={`question-${question.Id}`}
                  value={option}
                  checked={value === option}
                  onChange={() => onChange(option)}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-gray-700 break-words">{option}</span>
              </label>
            ))}
          </div>
        );

      case 6: // HiddenField
        return (
          <input 
            type="hidden" 
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          />
        );

      case 7: // NumberField
        return (
          <input 
            type="number" 
            {...baseProps}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            min={question.MinValue}
            max={question.MaxValue}
          />
        );

      case 8: // EmailField
        return (
          <input 
            type="email" 
            {...baseProps}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          />
        );

      case 9: // Checkboxes
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-3">
            {question.Options?.map((option, index) => (
              <label key={index} className="flex items-center gap-3 p-3 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  value={option}
                  checked={selectedValues.includes(option)}
                  onChange={(e) => {
                    const newValue = e.target.checked
                      ? [...selectedValues, option]
                      : selectedValues.filter(v => v !== option);
                    onChange(newValue);
                  }}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500 rounded"
                />
                <span className="text-gray-700 break-words">{option}</span>
              </label>
            ))}
          </div>
        );

      case 10: // SelectList
        return (
          <select 
            {...baseProps}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">Select an option</option>
            {question.Options?.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case 11: // FileUpload
        return (
          <input 
            type="file" 
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
              error ? 'border-red-300 bg-red-50' : 'border-gray-300'
            }`}
            onChange={(e) => {
              const file = e.target.files[0];
              onChange(file ? file.name : '');
            }}
          />
        );

      case 12: // NetPromoterScore
        return (
          <div className="bg-gray-50 rounded-lg p-4 sm:p-6 border border-gray-200">
            <div className="flex justify-between text-xs sm:text-sm text-gray-600 mb-4">
              <span className="break-words">Not at all likely (0)</span>
              <span className="break-words">Extremely likely (10)</span>
            </div>
            <div className="flex justify-center gap-1 sm:gap-2 overflow-x-auto pb-2">
              {Array.from({ length: 11 }, (_, i) => i).map((score) => (
                <label key={score} className="flex flex-col items-center gap-1 cursor-pointer flex-shrink-0">
                  <input
                    type="radio"
                    name={`question-${question.Id}`}
                    value={score}
                    checked={value === score}
                    onChange={() => onChange(score)}
                    className="sr-only"
                  />
                  <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full border-2 flex items-center justify-center text-xs sm:text-sm font-medium transition-all ${
                    value === score 
                      ? 'bg-blue-500 text-white border-blue-500' 
                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-500'
                  }`}>
                    {score}
                  </div>
                </label>
              ))}
            </div>
          </div>
        );

      case 13: // Signature
        return (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
            <p className="text-gray-600 mb-2">Signature field - Click to sign</p>
            <button 
              type="button"
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              onClick={() => onChange('Signed')}
            >
              Add Signature
            </button>
          </div>
        );

      case 14: // Paragraph
        return (
          <div className="text-gray-700 bg-gray-50 p-4 rounded-lg border border-gray-200 break-words">
            {question.QuestionText}
          </div>
        );

      default:
        return (
          <input 
            type="text" 
            {...baseProps}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          />
        );
    }
  };

  const isContentOnly = question.Type === 0 || question.Type === 14;

  if (isContentOnly) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        {renderInput()}
      </div>
    );
  }

  return (
    <div 
      className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 hover:shadow-sm transition-shadow overflow-hidden"
      data-error={!!error}
    >
      <div className="mb-4">
        <div className="flex items-start gap-3">
          <div className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium flex-shrink-0">
            {categoryNumber}.{questionNumber}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 mb-2 break-words">
              {question.QuestionText}
              {question.IsRequired && <span className="text-red-500 ml-1">*</span>}
            </h3>
            {question.HelpText && (
              <p className="text-gray-600 text-sm bg-blue-50 p-3 rounded-lg border border-blue-200 break-words">
                {question.HelpText}
              </p>
            )}
          </div>
        </div>
      </div>
      
      <div className="question-input-container w-full overflow-hidden">
        {renderInput()}
        
        {/* Error Message */}
        {error && (
          <div className="mt-2 flex items-center gap-2 text-red-600 text-sm">
            <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicSurveyForm;